/**
 * Tenant Settings (M27 PR 1) — /settings/tenant.
 *
 * Three cards:
 *   1. Identity — read-only (customer group name EN/AR, slug,
 *      region, created_at). "Contact support to rename" link.
 *   2. Defaults — editable for Tenant Admin (timezone, currency,
 *      date display preference, default language, billing email).
 *      A sticky save bar appears when any field has unsaved
 *      changes; toast on save.
 *   3. Stats — 4 brand-tonal KPI tiles (active users, total
 *      employees, legal entities, active contracts).
 *
 * Permission boundary is enforced server-side; the page itself
 * doesn't gate the save button (a denied PUT surfaces the 403
 * detail as a toast).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Briefcase,
  Building2,
  Check,
  FileText,
  Globe,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../../components/Loading";
import { KPICard, PageHeader } from "../../components/ui";
import { api, ApiError } from "../../lib/api";
import type { TenantSettings, TenantSettingsEditable } from "../../lib/api";

const _TIMEZONES = [
  "Asia/Riyadh",
  "Asia/Dubai",
  "Asia/Kuwait",
  "Asia/Bahrain",
  "Asia/Qatar",
  "Africa/Cairo",
  "Europe/London",
  "America/New_York",
];

const _CURRENCIES = ["SAR", "AED", "USD", "EUR", "EGP"];

export function TenantSettingsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["tenant-settings"],
    queryFn: () => api.getTenantSettings(),
  });

  // Local draft of the editable fields. Initialized from the
  // server response; tracks dirty state to gate the sticky save
  // bar.
  const [draft, setDraft] = useState<TenantSettingsEditable | null>(null);
  const [toast, setToast] = useState<{
    kind: "ok" | "err";
    message: string;
  } | null>(null);

  useEffect(() => {
    if (q.data && draft === null) {
      setDraft({
        billing_contact_email: q.data.billing_contact_email,
        timezone: q.data.timezone,
        default_currency: q.data.default_currency,
        date_display_preference: q.data.date_display_preference,
        default_language: q.data.default_language,
      });
    }
  }, [q.data, draft]);

  const dirty = useMemo(() => {
    if (!q.data || !draft) return false;
    return (
      draft.billing_contact_email !== q.data.billing_contact_email ||
      draft.timezone !== q.data.timezone ||
      draft.default_currency !== q.data.default_currency ||
      draft.date_display_preference !== q.data.date_display_preference ||
      draft.default_language !== q.data.default_language
    );
  }, [draft, q.data]);

  const save = useMutation({
    mutationFn: () => api.updateTenantSettings(draft as TenantSettingsEditable),
    onSuccess: (next) => {
      qc.setQueryData(["tenant-settings"], next);
      setDraft({
        billing_contact_email: next.billing_contact_email,
        timezone: next.timezone,
        default_currency: next.default_currency,
        date_display_preference: next.date_display_preference,
        default_language: next.default_language,
      });
      setToast({ kind: "ok", message: "Settings saved." });
      // Auto-dismiss after 3s so the bar doesn't linger.
      setTimeout(() => setToast(null), 3000);
    },
    onError: (e) => {
      setToast({
        kind: "err",
        message: e instanceof ApiError ? e.detail : String(e),
      });
    },
  });

  if (q.isLoading || !q.data || !draft) return <Loading />;
  if (q.error) return <ErrorPanel message={(q.error as Error).message} />;

  const tenant: TenantSettings = q.data;

  return (
    <div className="flex flex-col gap-5 pb-24">
      <PageHeader
        title={t("app.settings_tenant_title")}
        subtitle={t("app.settings_tenant_subtitle")}
        breadcrumbs={[
          { label: t("app.settings_breadcrumb"), href: "/settings" },
          { label: t("app.settings_tenant_title") },
        ]}
      />

      {/* ---- Identity card (read-only) ------------------------- */}
      <section className="rounded-xl border border-border-subtle bg-surface-default p-5">
        <header className="mb-4 flex items-center gap-2">
          <Building2 size={16} className="text-brand-blue" aria-hidden />
          <h2 className="text-sm font-semibold text-text-primary">
            {t("app.ts_identity")}
          </h2>
          <a
            href="mailto:support@solvait.ai?subject=Tenant rename request"
            className="ms-auto text-xs text-brand hover:underline"
          >
            {t("app.ts_contact_rename")}
          </a>
        </header>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <ReadOnlyField
            label={t("app.ts_group_en")}
            value={tenant.customer_group_name_en}
          />
          <ReadOnlyField
            label={t("app.ts_group_ar")}
            value={tenant.customer_group_name_ar}
            dir="rtl"
          />
          <ReadOnlyField
            label={t("app.ts_workspace_url")}
            value={`${tenant.slug}.solvait.ai`}
            hint={t("app.ts_workspace_hint")}
          />
          <ReadOnlyField
            label={t("app.ts_data_residency")}
            value={tenant.region}
            hint={
              tenant.region.toLowerCase() === "bahrain"
                ? t("app.ts_pdpl_hint")
                : undefined
            }
          />
          <ReadOnlyField
            label={t("app.ts_active_since")}
            value={new Date(tenant.created_at).toLocaleDateString()}
          />
        </dl>
      </section>

      {/* ---- Defaults card (editable) -------------------------- */}
      <section className="rounded-xl border border-border-subtle bg-surface-default p-5">
        <header className="mb-4 flex items-center gap-2">
          <Globe size={16} className="text-brand-blue" aria-hidden />
          <h2 className="text-sm font-semibold text-text-primary">
            {t("app.ts_defaults")}
          </h2>
          <span className="ms-auto text-xs text-text-tertiary">
            {t("app.ts_admin_edit")}
          </span>
        </header>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <EditableField
            label={t("app.ts_billing_email")}
            type="email"
            value={draft.billing_contact_email}
            onChange={(v) =>
              setDraft({ ...draft, billing_contact_email: v })
            }
          />

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-text-secondary">
              {t("app.ts_timezone")}
            </span>
            <select
              value={draft.timezone}
              onChange={(e) =>
                setDraft({ ...draft, timezone: e.target.value })
              }
              className="rounded border border-border-subtle bg-surface-default px-2 py-1.5 text-sm"
            >
              {_TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-text-secondary">
              {t("app.ts_default_currency")}
            </span>
            <select
              value={draft.default_currency}
              onChange={(e) =>
                setDraft({ ...draft, default_currency: e.target.value })
              }
              className="rounded border border-border-subtle bg-surface-default px-2 py-1.5 text-sm"
            >
              {_CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>

          <RadioGroup
            label={t("app.ts_date_display")}
            value={draft.date_display_preference}
            onChange={(v) =>
              setDraft({
                ...draft,
                date_display_preference:
                  v as TenantSettingsEditable["date_display_preference"],
              })
            }
            options={[
              { value: "gregorian_primary", label: t("app.ts_date_greg") },
              { value: "hijri_primary", label: t("app.ts_date_hijri") },
              { value: "both", label: t("app.ts_date_both") },
            ]}
          />

          <RadioGroup
            label={t("app.ts_default_lang")}
            value={draft.default_language}
            onChange={(v) =>
              setDraft({
                ...draft,
                default_language: v as "en" | "ar",
              })
            }
            options={[
              { value: "en", label: t("app.ts_lang_en") },
              { value: "ar", label: t("app.ts_lang_ar") },
            ]}
            hint={t("app.ts_lang_hint")}
          />
        </div>
      </section>

      {/* ---- Stats card --------------------------------------- */}
      <section>
        <header className="mb-3 flex items-center gap-2">
          <FileText size={16} className="text-brand-blue" aria-hidden />
          <h2 className="text-sm font-semibold text-text-primary">
            {t("app.ts_stats")}
          </h2>
        </header>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <KPICard
            tone="indigo"
            icon={Users}
            label={t("app.ts_active_users_30d")}
            count={tenant.stats.active_users_count}
            linkTo="/settings/users"
          />
          <KPICard
            tone="teal"
            icon={Users}
            label={t("app.ts_total_employees")}
            count={tenant.stats.total_employees}
            linkTo="/people/employees"
          />
          <KPICard
            tone="purple"
            icon={Building2}
            label={t("app.ts_legal_entities")}
            count={tenant.stats.total_legal_entities}
            linkTo="/settings/legal-entities"
          />
          <KPICard
            tone="neutral"
            icon={Briefcase}
            label={t("app.ts_active_contracts")}
            count={tenant.stats.total_active_contracts}
            linkTo="/people/contracts"
          />
        </div>
      </section>

      {/* ---- Sticky save bar ---------------------------------- */}
      {dirty && (
        <div
          role="region"
          aria-label={t("app.ts_unsaved_aria")}
          className="fixed inset-x-0 bottom-0 z-40 border-t border-border-subtle bg-surface-default px-4 py-3 shadow-lg"
          style={{ marginInlineEnd: "var(--chat-panel-width, 0px)" }}
        >
          <div className="mx-auto flex max-w-workspace items-center gap-3">
            <span className="text-sm text-text-secondary">
              {t("app.ts_unsaved_msg")}
            </span>
            <button
              type="button"
              onClick={() =>
                setDraft({
                  billing_contact_email: tenant.billing_contact_email,
                  timezone: tenant.timezone,
                  default_currency: tenant.default_currency,
                  date_display_preference: tenant.date_display_preference,
                  default_language: tenant.default_language,
                })
              }
              className="ms-auto rounded border border-border-subtle px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-sunken"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="rounded bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg hover:opacity-90 disabled:opacity-50"
            >
              {save.isPending ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      )}

      {/* ---- Toast -------------------------------------------- */}
      {toast && (
        <div
          role="status"
          className={`fixed bottom-20 right-4 z-50 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm shadow-md ${
            toast.kind === "ok"
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border-rose-200 bg-rose-50 text-rose-800"
          }`}
        >
          {toast.kind === "ok" && <Check size={14} aria-hidden />}
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Local field components — kept inline; small and only used here.
// ---------------------------------------------------------------------------

function ReadOnlyField({
  label,
  value,
  hint,
  dir,
}: {
  label: string;
  value: string;
  hint?: string;
  dir?: "ltr" | "rtl";
}) {
  return (
    <div>
      <dt className="text-xs font-medium text-text-secondary">{label}</dt>
      <dd
        className="mt-1 text-sm text-text-primary"
        dir={dir}
      >
        {value}
      </dd>
      {hint && (
        <p className="mt-0.5 text-xs text-text-tertiary">{hint}</p>
      )}
    </div>
  );
}

function EditableField({
  label,
  value,
  type = "text",
  onChange,
}: {
  label: string;
  value: string;
  type?: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-text-secondary">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded border border-border-subtle bg-surface-default px-2 py-1.5 text-sm"
      />
    </label>
  );
}

function RadioGroup({
  label,
  value,
  onChange,
  options,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
  hint?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-text-secondary">{label}</span>
      <div className="inline-flex flex-wrap items-center gap-1 rounded-md border border-border-subtle bg-surface-default p-0.5">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={`rounded px-2.5 py-1 text-xs font-medium ${
              value === o.value
                ? "bg-brand text-brand-fg"
                : "text-text-secondary hover:bg-surface-sunken"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
      {hint && (
        <p className="mt-0.5 text-xs text-text-tertiary">{hint}</p>
      )}
    </div>
  );
}
